"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import { BUILD_WITH_AI, BUILD_WITH_AI_TITLE } from "../_content/build-with-ai";
import { BANDS, isLine } from "../_content/card";
import { GUIDES } from "../_content/guides";
import { REFERENCES } from "../_content/reference";
import { Icon } from "../_family/Icon";
import { PageMenu } from "./PageMenu";

type Section = "start" | "guides" | "card" | "reference";

function sectionOf(path: string): Section {
  if (path.startsWith("/developers/guides")) return "guides";
  if (
    path.startsWith("/developers/reference") ||
    path === "/developers/changelog"
  )
    return "reference";
  if (isLine(path.slice("/developers/".length))) return "card";
  return "start";
}

/** What the phone's sidebar button says: where you are. */
function hereOf(path: string): string {
  const rest = path.slice("/developers/".length);
  if (isLine(rest)) return `Commands: ${rest}`;
  const guide = GUIDES.find((g) => path === `/developers/guides/${g.slug}`);
  if (guide) return guide.nav;
  if (path === "/developers/guides") return "Guides";
  if (path === "/developers/quickstart") return "Quickstart";
  if (path === BUILD_WITH_AI) return BUILD_WITH_AI_TITLE;
  if (path === "/developers/changelog") return "Changelog";
  const ref = REFERENCES.find(
    (r) => path === r.href || path.startsWith(`${r.href}/`)
  );
  if (ref) return `Reference: ${ref.title}`;
  if (path.startsWith("/developers/reference")) return "Reference";
  return "Developer docs";
}

function Group({
  name,
  open,
  children,
}: {
  name: string;
  open: boolean;
  children: ReactNode;
}) {
  return (
    <details open={open}>
      <summary>
        {name}
        <Icon name="caretRight" />
      </summary>
      {children}
    </details>
  );
}

/** The sidebar: four groups, closed but for the one you are in. */
function Side({ path, onGo }: { path: string; onGo: () => void }) {
  const section = sectionOf(path);
  const here = (href: string) => (path === href ? "page" : undefined);
  // An API group's page is in the API: the sidebar marks the API while you read one.
  const within = (href: string) =>
    path === href || path.startsWith(`${href}/`) ? "page" : undefined;
  const item = (
    label: ReactNode,
    href: string,
    current: "page" | undefined = here(href)
  ) => (
    <li key={href}>
      <Link href={href} aria-current={current} onClick={onGo}>
        {label}
      </Link>
    </li>
  );
  return (
    <aside className="side" id="docs-side" aria-label="Developer docs">
      <nav aria-label="Docs">
        <Group name="Start" open={section === "start"}>
          <ul>
            {item("Overview", "/developers")}
            {item("Quickstart", "/developers/quickstart")}
            {item(BUILD_WITH_AI_TITLE, BUILD_WITH_AI)}
            <li>
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- the console sits behind the edge's sign-in: a full page load */}
              <a href="/developers/console">Get a test key</a>
            </li>
          </ul>
        </Group>
        <Group name="Guides" open={section === "guides"}>
          <ul>
            {item("All guides", "/developers/guides")}
            {GUIDES.map((g) => item(g.nav, `/developers/guides/${g.slug}`))}
          </ul>
        </Group>
        <Group name="Commands" open={section === "card"}>
          {BANDS.map((band) => (
            <div key={band.id}>
              <p className="sub">{band.label}</p>
              <ul>
                {band.lines.map((line) => (
                  <li key={line} className="cmd">
                    <Link
                      href={`/developers/${line}`}
                      aria-current={here(`/developers/${line}`)}
                      onClick={onGo}
                    >
                      {line}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Group>
        <Group name="Reference" open={section === "reference"}>
          <ul>
            {item("Overview", "/developers/reference")}
            {REFERENCES.map((r) => item(r.title, r.href, within(r.href)))}
            {item("Changelog", "/developers/changelog")}
          </ul>
        </Group>
      </nav>
    </aside>
  );
}

/**
 * The docs' frame under the top bar: the sidebar beside the page on a wide screen, and behind a button on a
 * phone, where a new page or Escape puts it away; above every page, its menu (copy it as Markdown, or open it).
 */
export function DocsShell({ children }: { children: ReactNode }) {
  const path = usePathname() ?? "";
  // Open on the page it was opened on: a new page closes it, with no state to reset.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const open = openOn === path;

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenOn(null);
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  return (
    <div className={open ? "shell side-open" : "shell"}>
      <Side path={path} onGo={() => setOpenOn(null)} />
      <main className="main" id="docs-main" tabIndex={-1}>
        <div className="pagebar">
          <button
            className="side-btn"
            type="button"
            aria-expanded={open}
            aria-controls="docs-side"
            onClick={() => setOpenOn(open ? null : path)}
          >
            <Icon name="list" />
            {hereOf(path)}
          </button>
          <PageMenu />
        </div>
        {children}
      </main>
    </div>
  );
}
